import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import {
  Users, DollarSign, Activity, AlertTriangle, BarChart3, Settings,
  MessageSquare, FileText, Shield, TrendingUp, Calendar, Search,
  Filter, Download, Eye, Edit, Trash2, CheckCircle, XCircle,
  Clock, Bell, Mail, Database, Globe, Zap, Target, Crown,
  MoreVertical, RefreshCw, ArrowUp, ArrowDown, UserCheck,
  UserX, Ban, Flag, Star, Heart, MessageCircle, Share2, Send, Tag, Percent
} from 'lucide-react';

// Import UI components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';

const AdminPortal = () => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [contentModeration, setContentModeration] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  // Coupon management state
  const [coupons, setCoupons] = useState([]);
  const [couponRedemptions, setCouponRedemptions] = useState([]);
  const [couponStats, setCouponStats] = useState(null);
  const [showCreateCouponDialog, setShowCreateCouponDialog] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    max_redemptions: '',
    expires_at: '',
    applicable_tiers: ['seekers', 'crown'],
    description: ''
  });
  const [creatingCoupon, setCreatingCoupon] = useState(false);
  
  // Announcement form state
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    target_audience: 'all',
    priority: 'info'
  });

  // Admin settings state
  const [adminSettings, setAdminSettings] = useState({
    userRegistration: true,
    contentModerationMode: 'manual', // 'auto' or 'manual'
    aiToolsEnabled: true
  });
  
  // Filters and pagination
  const [userFilters, setUserFilters] = useState({
    search: '',
    status: '',
    region: '',
    page: 1,
    limit: 20
  });
  
  const [ticketFilters, setTicketFilters] = useState({
    status: '',
    priority: '',
    page: 1,
    limit: 20
  });

  // Ticket reply state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReplies, setTicketReplies] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);

  // Check if user is admin
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    
    // For demo purposes, check if user email contains 'admin' or has admin role
    const isAdmin = user.email?.includes('admin') || user.role === 'admin';
    if (!isAdmin) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "Admin access required to view this page"
      });
      setLoading(false);
      return;
    }
    
    loadDashboardData();
  }, [isAuthenticated, user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      console.log('Admin Portal: Starting to load dashboard data...');
      
      // Load dashboard statistics
      console.log('Admin Portal: Loading dashboard stats...');
      const dashboardResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/dashboard`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      console.log('Admin Portal: Dashboard response status:', dashboardResponse.status);
      
      if (dashboardResponse.ok) {
        const dashboardData = await dashboardResponse.json();
        console.log('Admin Portal: Dashboard data received:', dashboardData);
        setDashboardStats(dashboardData.dashboard_stats);
      } else {
        console.error('Admin Portal: Dashboard request failed:', dashboardResponse.status, await dashboardResponse.text());
        toast({
          variant: "destructive",
          title: "Dashboard Error",
          description: `Failed to load dashboard: ${dashboardResponse.status}`
        });
      }
      
      // Load users data
      console.log('Admin Portal: Loading users...');
      await loadUsers();
      
      // Load support tickets
      console.log('Admin Portal: Loading support tickets...');
      await loadSupportTickets();
      
      // Load analytics
      console.log('Admin Portal: Loading analytics...');
      await loadAnalytics();
      
      // Load announcements
      console.log('Admin Portal: Loading announcements...');
      await loadAnnouncements();
      
      console.log('Admin Portal: All data loaded successfully');
      
    } catch (error) {
      console.error('Admin Portal: Error loading admin data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load admin dashboard data"
      });
    } finally {
      console.log('Admin Portal: Setting loading to false');
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      console.log('Admin Portal: loadUsers starting...');
      const params = new URLSearchParams(userFilters);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      console.log('Admin Portal: loadUsers response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        console.log('Admin Portal: loadUsers completed successfully');
      } else {
        console.log('Admin Portal: loadUsers failed with status:', response.status);
      }
    } catch (error) {
      console.error('Admin Portal: Error loading users:', error);
    }
  };

  const loadSupportTickets = async () => {
    try {
      const params = new URLSearchParams(ticketFilters);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSupportTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Error loading support tickets:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/analytics?period=30d`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleUserAction = async (userId, action, reason) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/users/${userId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ action, reason })
      });
      
      if (!response.ok) {
        throw new Error('Failed to perform user action');
      }
      
      await loadUsers();
      toast({
        title: "Success",
        description: `User ${action}d successfully`
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to perform action: ${error.message}`
      });
    }
  };

  const handleUserDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE "${userName}"? This will remove all their data including posts, photos, and profile information. This action CANNOT be undone!`)) {
      return;
    }
    
    // Double confirmation for such a destructive action
    if (!window.confirm('FINAL CONFIRMATION: This will permanently delete all user data. Type YES to continue.')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete user');
      }
      
      const result = await response.json();
      await loadUsers();
      toast({ 
        title: "Success", 
        description: `User deleted successfully. Removed ${Object.keys(result.deleted_records || {}).length} associated records.`
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to delete user: ${error.message}`
      });
    }
  };

  const handleTicketUpdate = async (ticketId, updateData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets/${ticketId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        toast({
          title: "Success",
          description: "Ticket updated successfully"
        });
        await loadSupportTickets(); // Reload tickets
      } else {
        throw new Error('Failed to update ticket');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to update ticket: ${error.message}`
      });
    }
  };

  const handleTicketDelete = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this support ticket? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets/${ticketId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete ticket');
      }
      
      await loadSupportTickets();
      toast({ title: "Success", description: "Ticket deleted successfully" });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to delete ticket: ${error.message}`
      });
    }
  };

  // Load ticket replies
  const loadTicketReplies = async (ticketId) => {
    setLoadingReplies(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets/${ticketId}/replies`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTicketReplies(data.replies || []);
      }
    } catch (error) {
      console.error('Error loading replies:', error);
    } finally {
      setLoadingReplies(false);
    }
  };

  // Send ticket reply
  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets/${selectedTicket.id}/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ message: replyMessage })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Add the new reply to the list
        setTicketReplies(prev => [...prev, data.reply]);
        setReplyMessage('');
        toast({ title: "Success", description: "Reply sent successfully" });
        // Reload tickets to update status
        await loadSupportTickets();
      } else {
        throw new Error('Failed to send reply');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to send reply: ${error.message}`
      });
    } finally {
      setSendingReply(false);
    }
  };

  // Open ticket thread
  const openTicketThread = async (ticket) => {
    setSelectedTicket(ticket);
    setReplyMessage('');
    await loadTicketReplies(ticket.id);
  };

  // Close ticket thread
  const closeTicketThread = () => {
    setSelectedTicket(null);
    setTicketReplies([]);
    setReplyMessage('');
  };

  const handleCreateAnnouncement = async () => {
    try {
      if (!announcementForm.title.trim() || !announcementForm.message.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Please fill in both title and message"
        });
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/announcements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          title: announcementForm.title.trim(),
          message: announcementForm.message.trim(),
          target_audience: announcementForm.target_audience,
          priority: announcementForm.priority,
          created_by: user?.id || 'admin'
        })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Announcement created successfully"
        });
        
        // Reset form
        setAnnouncementForm({
          title: '',
          message: '',
          target_audience: 'all',
          priority: 'info'
        });
        
        // Reload announcements
        await loadAnnouncements();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create announcement');
      }
    } catch (error) {
      console.error('Error creating announcement:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to create announcement: ${error.message}`
      });
    }
  };

  const loadAnnouncements = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/announcements`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
  };

  // Coupon Management Functions
  const loadCoupons = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/list`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCoupons(data || []);
      }
    } catch (error) {
      console.error('Error loading coupons:', error);
    }
  };

  const loadCouponRedemptions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/redemptions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCouponRedemptions(data || []);
      }
    } catch (error) {
      console.error('Error loading coupon redemptions:', error);
    }
  };

  const loadCouponStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCouponStats(data);
      }
    } catch (error) {
      console.error('Error loading coupon stats:', error);
    }
  };

  const handleCreateCoupon = async () => {
    if (!couponForm.code || !couponForm.discount_value) {
      toast({
        variant: "destructive",
        title: "Missing Fields",
        description: "Please fill in code and discount value"
      });
      return;
    }

    setCreatingCoupon(true);
    try {
      const payload = {
        code: couponForm.code.toUpperCase(),
        discount_type: couponForm.discount_type,
        discount_value: parseFloat(couponForm.discount_value),
        max_redemptions: couponForm.max_redemptions ? parseInt(couponForm.max_redemptions) : null,
        expires_at: couponForm.expires_at || null,
        applicable_tiers: couponForm.applicable_tiers,
        description: couponForm.description || null
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast({
          title: "Coupon Created!",
          description: `Promo code ${couponForm.code.toUpperCase()} is now active`
        });
        setShowCreateCouponDialog(false);
        setCouponForm({
          code: '',
          discount_type: 'percentage',
          discount_value: '',
          max_redemptions: '',
          expires_at: '',
          applicable_tiers: ['seekers', 'crown'],
          description: ''
        });
        loadCoupons();
        loadCouponStats();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create coupon');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message
      });
    } finally {
      setCreatingCoupon(false);
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/${couponId}/toggle`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        toast({
          title: "Coupon Updated",
          description: "Coupon status has been toggled"
        });
        loadCoupons();
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to toggle coupon status"
      });
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to deactivate this coupon?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/${couponId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        toast({
          title: "Coupon Deactivated",
          description: "Coupon has been deactivated"
        });
        loadCoupons();
        loadCouponStats();
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete coupon"
      });
    }
  };

  // Load coupons on component mount
  useEffect(() => {
    if (user && (user.email?.includes('admin') || user.role === 'admin')) {
      loadCoupons();
      loadCouponRedemptions();
      loadCouponStats();
    }
  }, [user]);


  const toggleUserRegistration = async () => {
    const newValue = !adminSettings.userRegistration;
    setAdminSettings(prev => ({ ...prev, userRegistration: newValue }));
    
    toast({
      title: "Setting Updated",
      description: `User registration ${newValue ? 'enabled' : 'disabled'}`
    });
  };

  const toggleContentModerationMode = async () => {
    const newMode = adminSettings.contentModerationMode === 'auto' ? 'manual' : 'auto';
    setAdminSettings(prev => ({ ...prev, contentModerationMode: newMode }));
    
    toast({
      title: "Content Moderation Updated",
      description: `Content moderation set to ${newMode === 'auto' ? 'automatic approval' : 'manual review'}`
    });
  };

  const toggleAITools = async () => {
    const newValue = !adminSettings.aiToolsEnabled;
    setAdminSettings(prev => ({ ...prev, aiToolsEnabled: newValue }));
    
    toast({
      title: "AI Tools Updated",
      description: `AI tools ${newValue ? 'enabled' : 'disabled'}`
    });
  };

  const handleUserRoleToggle = async (userId, isCurrentlyAdmin) => {
    try {
      const newRole = isCurrentlyAdmin ? 'user' : 'admin';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          role: newRole,
          is_admin: !isCurrentlyAdmin
        })
      });

      if (response.ok) {
        toast({
          title: "Role Updated",
          description: `User role changed to ${newRole}`
        });
        
        // Refresh users list
        await loadUsers();
      } else {
        throw new Error('Failed to update user role');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: "Failed to update user role. Please try again."
      });
    }
  };

  const handleExportData = async (type) => {
    try {
      toast({
        title: "Export Started",
        description: `Preparing ${type} export. You will be notified when ready for download.`
      });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/export/${type.toLowerCase().replace(' ', '-')}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type.toLowerCase().replace(' ', '_')}_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        toast({
          title: "Export Complete",
          description: `${type} export downloaded successfully.`
        });
      } else {
        throw new Error(`Export failed: ${response.statusText}`);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Export Failed", 
        description: `Failed to export ${type}. Please try again.`
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Admin Portal...</p>
        </div>
      </div>
    );
  }

  if (!user?.email?.includes('admin') && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-red-600">Access Denied</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">Admin access required to view this page.</p>
            <Button onClick={() => window.history.back()}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      <Header />
      <div className="p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Crown className="w-8 h-8 text-amber-600 mr-3" />
                TribalRoot.AI Admin Portal
              </h1>
              <p className="text-gray-600 mt-2">Central command hub for platform oversight and management</p>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                onClick={loadDashboardData}
                variant="outline"
                size="sm"
                className="flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Badge variant="secondary" className="text-sm">
                Admin: {user?.full_name || user?.email}
              </Badge>
            </div>
          </div>
        </div>

        {/* Dashboard Stats */}
        {dashboardStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <Users className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardStats.total_users.toLocaleString()}</div>
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  +{dashboardStats.new_signups_today} today
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${dashboardStats.monthly_revenue.toLocaleString()}</div>
                <p className="text-xs text-blue-600 flex items-center mt-1">
                  <Crown className="w-3 h-3 mr-1" />
                  {dashboardStats.active_subscriptions} active subscriptions
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                <Activity className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardStats.active_users_today.toLocaleString()}</div>
                <p className="text-xs text-gray-600">Active in last 30 days</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Open Tickets</CardTitle>
                <MessageSquare className="h-4 w-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardStats.open_support_tickets}</div>
                <p className="text-xs text-amber-600 flex items-center mt-1">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {dashboardStats.pending_content_reviews} pending reviews
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="users" className="flex items-center">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="coupons" className="flex items-center">
              <Tag className="w-4 h-4 mr-2" />
              Coupons
            </TabsTrigger>
            <TabsTrigger value="tickets" className="flex items-center">
              <MessageSquare className="w-4 h-4 mr-2" />
              Support
            </TabsTrigger>
            <TabsTrigger value="announcements" className="flex items-center">
              <Bell className="w-4 h-4 mr-2" />
              Announce
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Users Management */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  User Management
                </CardTitle>
                <div className="flex items-center space-x-4 mt-4">
                  <Input
                    placeholder="Search users..."
                    value={userFilters.search}
                    onChange={(e) => setUserFilters({...userFilters, search: e.target.value})}
                    className="max-w-xs"
                  />
                  <Select value={userFilters.status} onValueChange={(value) => setUserFilters({...userFilters, status: value})}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="banned">Banned</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={loadUsers} size="sm">
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <Avatar>
                          <AvatarFallback>
                            {user.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 
                            className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer underline" 
                            onClick={() => window.open(`/public-profile/${user.id}`, '_blank')}
                          >
                            {user.full_name}
                          </h3>
                          <p className="text-sm text-gray-600">{user.email}</p>
                          {user.tribal_name && (
                            <p className="text-sm text-blue-600">Tribal: {user.tribal_name}</p>
                          )}
                          {user.region_flag && (
                            <Badge variant="outline" className="text-xs mt-1">{user.region_flag}</Badge>
                          )}
                          {/* Role Management */}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-gray-500">Role:</span>
                            <Button
                              size="sm"
                              variant={user.role === 'admin' || user.is_admin ? "default" : "outline"}
                              onClick={() => handleUserRoleToggle(user.id, user.role === 'admin' || user.is_admin)}
                              className="h-6 px-2 text-xs"
                            >
                              {user.role === 'admin' || user.is_admin ? 'Admin' : 'User'}
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={user.status === 'active' ? 'default' : 'destructive'}>
                          {user.status}
                        </Badge>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>User Actions - {user.full_name}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-2">
                                <Button 
                                  onClick={() => handleUserAction(user.id, 'activate', 'Admin activation')}
                                  variant="outline"
                                  className="flex items-center"
                                  disabled={user.status === 'active'}
                                >
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Activate
                                </Button>
                                <Button 
                                  onClick={() => handleUserAction(user.id, 'suspend', 'Admin suspension')}
                                  variant="outline"
                                  className="flex items-center"
                                  disabled={user.status === 'suspended'}
                                >
                                  <UserX className="w-4 h-4 mr-2" />
                                  Suspend
                                </Button>
                                <Button 
                                  onClick={() => handleUserAction(user.id, 'verify', 'Admin verification')}
                                  variant="outline" 
                                  className="flex items-center"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Verify
                                </Button>
                                <Button 
                                  onClick={() => handleUserAction(user.id, 'ban', 'Policy violation')}
                                  variant="destructive"
                                  className="flex items-center"
                                  disabled={user.status === 'banned'}
                                >
                                  <Ban className="w-4 h-4 mr-2" />
                                  Ban
                                </Button>
                              </div>
                              <Separator />
                              <div className="space-y-2">
                                <p className="text-sm"><strong>Engagement Score:</strong> {user.engagement_score}%</p>
                                <p className="text-sm"><strong>Profile Completion:</strong> {user.profile_completion}%</p>
                                <p className="text-sm"><strong>Community Posts:</strong> {user.community_posts}</p>
                                <p className="text-sm"><strong>AI Tools Used:</strong> {user.ai_tools_used.join(', ')}</p>
                              </div>
                              <Separator />
                              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                                <h4 className="text-sm font-medium text-red-800 mb-2">Danger Zone</h4>
                                <Button 
                                  onClick={() => handleUserDelete(user.id, user.full_name)}
                                  variant="destructive"
                                  className="w-full flex items-center justify-center"
                                  size="sm"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Permanently Delete User
                                </Button>
                                <p className="text-xs text-red-600 mt-2">This will permanently delete all user data and cannot be undone.</p>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Dashboard */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Globe className="w-5 h-5 mr-2" />
                    User Activity by Region
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.user_activity_by_region && (
                    <div className="space-y-3">
                      {Object.entries(analytics.user_activity_by_region).slice(0, 8).map(([region, count]) => (
                        <div key={region} className="flex items-center justify-between">
                          <span className="text-sm font-medium">{region}</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${(count / Math.max(...Object.values(analytics.user_activity_by_region))) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600 w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Zap className="w-5 h-5 mr-2" />
                    AI Tool Usage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.ai_tool_usage && (
                    <div className="space-y-3">
                      {Object.entries(analytics.ai_tool_usage).map(([tool, count]) => (
                        <div key={tool} className="flex items-center justify-between">
                          <span className="text-sm font-medium">{tool}</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-600 h-2 rounded-full"
                                style={{ width: `${(count / Math.max(...Object.values(analytics.ai_tool_usage))) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Crown className="w-5 h-5 mr-2" />
                    Top Surnames
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.top_surnames && (
                    <div className="space-y-2">
                      {analytics.top_surnames.map((surname, index) => (
                        <div key={surname.surname} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Badge variant="outline" className="mr-2 w-6 h-6 flex items-center justify-center p-0">
                              {index + 1}
                            </Badge>
                            <span className="text-sm font-medium">{surname.surname}</span>
                          </div>
                          <span className="text-sm text-gray-600">{surname.count} families</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    Content Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.content_metrics && (
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-sm">Total Posts</span>
                        <span className="font-semibold">{analytics.content_metrics.total_posts}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Approved</span>
                        <span className="font-semibold text-green-600">{analytics.content_metrics.approved_posts}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Pending Review</span>
                        <span className="font-semibold text-yellow-600">{analytics.content_metrics.pending_review}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Flagged Content</span>
                        <span className="font-semibold text-red-600">{analytics.content_metrics.flagged_content}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Coupons Management */}
          <TabsContent value="coupons">
            <div className="space-y-6">
              {/* Coupon Stats */}
              {couponStats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Total Coupons</p>
                          <p className="text-2xl font-bold">{couponStats.total_coupons}</p>
                        </div>
                        <Tag className="w-8 h-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Active Coupons</p>
                          <p className="text-2xl font-bold text-green-600">{couponStats.active_coupons}</p>
                        </div>
                        <CheckCircle className="w-8 h-8 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Total Redemptions</p>
                          <p className="text-2xl font-bold">{couponStats.total_redemptions}</p>
                        </div>
                        <TrendingUp className="w-8 h-8 text-purple-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Total Discount Given</p>
                          <p className="text-2xl font-bold text-amber-600">${couponStats.total_discount_given}</p>
                        </div>
                        <DollarSign className="w-8 h-8 text-amber-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Create Coupon Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center">
                      <Tag className="w-5 h-5 mr-2" />
                      Coupon Management
                    </CardTitle>
                    <Dialog open={showCreateCouponDialog} onOpenChange={setShowCreateCouponDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-green-600 hover:bg-green-700">
                          <Tag className="w-4 h-4 mr-2" />
                          Create Coupon
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Create New Coupon</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <label className="text-sm font-medium">Promo Code *</label>
                            <Input
                              placeholder="e.g., WELCOME25"
                              value={couponForm.code}
                              onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})}
                              className="mt-1"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium">Discount Type *</label>
                              <Select 
                                value={couponForm.discount_type} 
                                onValueChange={(value) => setCouponForm({...couponForm, discount_type: value})}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                                  <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-sm font-medium">
                                Value * {couponForm.discount_type === 'percentage' ? '(%)' : '($)'}
                              </label>
                              <Input
                                type="number"
                                placeholder={couponForm.discount_type === 'percentage' ? '25' : '10.00'}
                                value={couponForm.discount_value}
                                onChange={(e) => setCouponForm({...couponForm, discount_value: e.target.value})}
                                className="mt-1"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium">Max Uses (optional)</label>
                              <Input
                                type="number"
                                placeholder="Unlimited"
                                value={couponForm.max_redemptions}
                                onChange={(e) => setCouponForm({...couponForm, max_redemptions: e.target.value})}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium">Expires (optional)</label>
                              <Input
                                type="datetime-local"
                                value={couponForm.expires_at}
                                onChange={(e) => setCouponForm({...couponForm, expires_at: e.target.value})}
                                className="mt-1"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium">Applicable Plans</label>
                            <div className="flex gap-4 mt-2">
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={couponForm.applicable_tiers.includes('seekers')}
                                  onChange={(e) => {
                                    const tiers = e.target.checked 
                                      ? [...couponForm.applicable_tiers, 'seekers']
                                      : couponForm.applicable_tiers.filter(t => t !== 'seekers');
                                    setCouponForm({...couponForm, applicable_tiers: tiers});
                                  }}
                                  className="mr-2"
                                />
                                Seekers Path
                              </label>
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={couponForm.applicable_tiers.includes('crown')}
                                  onChange={(e) => {
                                    const tiers = e.target.checked 
                                      ? [...couponForm.applicable_tiers, 'crown']
                                      : couponForm.applicable_tiers.filter(t => t !== 'crown');
                                    setCouponForm({...couponForm, applicable_tiers: tiers});
                                  }}
                                  className="mr-2"
                                />
                                Crown Plan
                              </label>
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium">Description (internal)</label>
                            <Textarea
                              placeholder="Internal notes about this coupon..."
                              value={couponForm.description}
                              onChange={(e) => setCouponForm({...couponForm, description: e.target.value})}
                              className="mt-1"
                              rows={2}
                            />
                          </div>
                          <Button 
                            onClick={handleCreateCoupon} 
                            className="w-full bg-green-600 hover:bg-green-700"
                            disabled={creatingCoupon}
                          >
                            {creatingCoupon ? 'Creating...' : 'Create Coupon'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Coupons Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Code</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Discount</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Uses</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Expires</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Plans</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coupons.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                              No coupons created yet. Click &quot;Create Coupon&quot; to get started.
                            </td>
                          </tr>
                        ) : (
                          coupons.map((coupon) => (
                            <tr key={coupon.id} className="border-t hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <span className="font-mono font-semibold text-blue-600">{coupon.code}</span>
                              </td>
                              <td className="px-4 py-3">
                                {coupon.discount_type === 'percentage' 
                                  ? `${coupon.discount_value}%` 
                                  : `$${coupon.discount_value}`}
                              </td>
                              <td className="px-4 py-3">
                                {coupon.times_redeemed} / {coupon.max_redemptions || '∞'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {coupon.expires_at 
                                  ? new Date(coupon.expires_at).toLocaleDateString()
                                  : 'Never'}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-1">
                                  {coupon.applicable_tiers?.map(tier => (
                                    <Badge key={tier} variant="outline" className="text-xs capitalize">
                                      {tier}
                                    </Badge>
                                  ))}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <Badge className={coupon.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                                  {coupon.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleToggleCoupon(coupon.id)}
                                  >
                                    {coupon.is_active ? 'Disable' : 'Enable'}
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="destructive"
                                    onClick={() => handleDeleteCoupon(coupon.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Redemption History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Redemption History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Code</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">User</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Plan</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Discount</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Final Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {couponRedemptions.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                              No redemptions yet
                            </td>
                          </tr>
                        ) : (
                          couponRedemptions.map((redemption) => (
                            <tr key={redemption.id} className="border-t hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">
                                {new Date(redemption.redeemed_at).toLocaleString()}
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-mono font-semibold text-blue-600">{redemption.coupon_code}</span>
                              </td>
                              <td className="px-4 py-3 text-sm">{redemption.user_email}</td>
                              <td className="px-4 py-3">
                                <Badge variant="outline" className="capitalize">{redemption.subscription_tier}</Badge>
                              </td>
                              <td className="px-4 py-3 text-green-600 font-medium">
                                -${redemption.discount_amount?.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 font-medium">
                                ${redemption.final_amount?.toFixed(2)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Support Tickets */}
          <TabsContent value="tickets">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Support Tickets
                </CardTitle>
                <div className="flex items-center space-x-4 mt-4">
                  <Select value={ticketFilters.status} onValueChange={(value) => setTicketFilters({...ticketFilters, status: value})}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={ticketFilters.priority} onValueChange={(value) => setTicketFilters({...ticketFilters, priority: value})}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priority</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={loadSupportTickets} size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Ticket Thread View */}
                {selectedTicket ? (
                  <div className="space-y-4">
                    {/* Back button and ticket info */}
                    <div className="flex items-center justify-between border-b pb-4">
                      <Button variant="outline" size="sm" onClick={closeTicketThread}>
                        ← Back to Tickets
                      </Button>
                      <div className="flex items-center space-x-2">
                        <Badge variant={selectedTicket.priority === 'urgent' ? 'destructive' : selectedTicket.priority === 'high' ? 'default' : 'secondary'}>
                          {selectedTicket.priority}
                        </Badge>
                        <Select defaultValue={selectedTicket.status} onValueChange={(value) => handleTicketUpdate(selectedTicket.id, {status: value})}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Ticket Subject and Original Message */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-2">{selectedTicket.subject}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                        <span>{selectedTicket.user_email}</span>
                        <span>•</span>
                        <span>{new Date(selectedTicket.created_at).toLocaleString()}</span>
                        {selectedTicket.category && (
                          <>
                            <span>•</span>
                            <Badge variant="outline" className="text-xs">{selectedTicket.category}</Badge>
                          </>
                        )}
                      </div>
                      <p className="text-gray-700">{selectedTicket.message}</p>
                    </div>

                    {/* Replies Thread */}
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {loadingReplies ? (
                        <div className="text-center py-4 text-gray-500">Loading replies...</div>
                      ) : ticketReplies.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">No replies yet. Start the conversation below.</div>
                      ) : (
                        ticketReplies.map((reply) => (
                          <div 
                            key={reply.id} 
                            className={`p-4 rounded-lg ${reply.is_admin ? 'bg-blue-50 ml-8' : 'bg-gray-100 mr-8'}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-sm">
                                  {reply.sender_name || reply.sender_email}
                                </span>
                                {reply.is_admin && (
                                  <Badge variant="outline" className="text-xs bg-blue-100">Admin</Badge>
                                )}
                              </div>
                              <span className="text-xs text-gray-500">
                                {new Date(reply.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{reply.message}</p>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Reply Input */}
                    <div className="border-t pt-4">
                      <div className="flex space-x-2">
                        <Textarea 
                          placeholder="Type your reply..." 
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          rows={3}
                          className="flex-1"
                        />
                      </div>
                      <div className="flex justify-end mt-2">
                        <Button 
                          onClick={handleSendReply}
                          disabled={!replyMessage.trim() || sendingReply}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {sendingReply ? 'Sending...' : 'Send Reply'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Tickets List View */
                  <div className="space-y-4">
                    {supportTickets.map((ticket) => (
                      <div key={ticket.id} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => openTicketThread(ticket)}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-medium text-lg">{ticket.subject}</h3>
                            <p className="text-sm text-gray-600 mb-2">{ticket.user_email}</p>
                            <p className="text-sm line-clamp-2">{ticket.message}</p>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <Badge variant={ticket.priority === 'urgent' ? 'destructive' : ticket.priority === 'high' ? 'default' : 'secondary'}>
                              {ticket.priority}
                            </Badge>
                            <Badge variant={ticket.status === 'open' ? 'destructive' : ticket.status === 'resolved' ? 'default' : 'secondary'}>
                              {ticket.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              {new Date(ticket.created_at).toLocaleDateString()}
                            </span>
                            {ticket.category && (
                              <Badge variant="outline" className="text-xs">{ticket.category}</Badge>
                            )}
                          </div>
                          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                            <Button variant="outline" size="sm" onClick={() => openTicketThread(ticket)}>
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Reply
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => handleTicketDelete(ticket.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements */}
          <TabsContent value="announcements">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Bell className="w-5 h-5 mr-2" />
                  System Announcements
                </CardTitle>
                <div className="flex justify-end">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button>
                        <Bell className="w-4 h-4 mr-2" />
                        Create Announcement
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Create System Announcement</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Input 
                          placeholder="Announcement title..." 
                          value={announcementForm.title}
                          onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                        />
                        <Textarea 
                          placeholder="Announcement message..." 
                          rows={4}
                          value={announcementForm.message}
                          onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
                        />
                        <div className="grid grid-cols-2 gap-4">
                          <Select 
                            value={announcementForm.target_audience} 
                            onValueChange={(value) => setAnnouncementForm({...announcementForm, target_audience: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Target audience" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Users</SelectItem>
                              <SelectItem value="region">By Region</SelectItem>
                              <SelectItem value="subscription">By Subscription</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select 
                            value={announcementForm.priority} 
                            onValueChange={(value) => setAnnouncementForm({...announcementForm, priority: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Priority" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="info">Info</SelectItem>
                              <SelectItem value="warning">Warning</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button className="w-full" onClick={handleCreateAnnouncement}>
                          <Bell className="w-4 h-4 mr-2" />
                          Send Announcement
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Announcements</h3>
                  <p className="text-gray-600">Create your first system announcement to communicate with users</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Admin Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium mb-4">Platform Configuration</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">User Registration</h4>
                          <p className="text-sm text-gray-600">Allow new user registrations</p>
                        </div>
                        <Button 
                          variant={adminSettings.userRegistration ? "default" : "outline"} 
                          size="sm"
                          onClick={toggleUserRegistration}
                        >
                          {adminSettings.userRegistration ? "Enabled" : "Disabled"}
                        </Button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">Content Moderation</h4>
                          <p className="text-sm text-gray-600">Auto-approve community content</p>
                        </div>
                        <Button 
                          variant={adminSettings.contentModerationMode === 'auto' ? "default" : "outline"} 
                          size="sm"
                          onClick={toggleContentModerationMode}
                        >
                          {adminSettings.contentModerationMode === 'auto' ? "Auto Approval" : "Manual Review"}
                        </Button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">AI Tools</h4>
                          <p className="text-sm text-gray-600">Enable AI-powered features</p>
                        </div>
                        <Button 
                          variant={adminSettings.aiToolsEnabled ? "default" : "outline"} 
                          size="sm"
                          onClick={toggleAITools}
                        >
                          {adminSettings.aiToolsEnabled ? "Enabled" : "Disabled"}
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="font-medium mb-4">Export & Reports</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Button 
                        variant="outline" 
                        className="flex items-center"
                        onClick={() => handleExportData('User Data')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export User Data
                      </Button>
                      <Button 
                        variant="outline" 
                        className="flex items-center"
                        onClick={() => handleExportData('Financial Report')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export Financial Report
                      </Button>
                      <Button 
                        variant="outline" 
                        className="flex items-center"
                        onClick={() => handleExportData('Analytics')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export Analytics
                      </Button>
                      <Button 
                        variant="outline" 
                        className="flex items-center"
                        onClick={() => handleExportData('Activity Logs')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export Activity Logs
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      </div>
    </div>
  );
};

export default AdminPortal;